import React, {useEffect} from "react";
import {capitalize} from "lodash";

import {Button, ButtonGroup, Dropdown, OverlayTrigger, Tooltip} from "react-bootstrap";
import {HiViewGrid} from "react-icons/hi";
import {FiDownload} from "react-icons/fi";
import {IoClose} from "react-icons/io5";
import {useListContext} from "../../contexts/ListContext";
import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {usePostHeatmapDownload, usePostScoresDownload} from "../../hooks/useDownloadData";
import {useEntrezMap} from "../../hooks/useEntrezMap";
import {AlertType, DataType, QGiQueryType} from "../../types/enums";
import {SearchOption} from "../../types/Types";
import {openDepMap, openGeneCards} from "../ExternalLinks";

const ListGenePicker = () => {
  const {selectedGenes, currentGene, setSelectedGenes, setCurrentGene, addAlert, removeAlert, selectedScreen, setSelectedScreen} = useTheCellMapContext();
  const {listState, setQueryType} = useListContext();
  const downloadScores = usePostScoresDownload();
  const downloadHeatmap = usePostHeatmapDownload();
  const entrezMap = useEntrezMap();

  /** Set dropdown filters to default whenever a new gene is added */
  useEffect(() => {
    setQueryType(QGiQueryType.All);
    setSelectedScreen(null);
  }, [selectedGenes]);

  const commonSelectItems: { label: string; type: QGiQueryType }[] = [
    {label: "All Interactions", type: QGiQueryType.All},
    {label: "Library Gene Interactions", type: QGiQueryType.Library},
    {label: "Query Gene Interactions", type: QGiQueryType.Query},
  ];

  const getActive = (gene: SearchOption) => currentGene?.symbol === gene.symbol ? "active" : "";

  const getScreens = (gene: SearchOption) => entrezMap?.[gene.value].g || [];

  const getEntrezIDs = (genes: readonly SearchOption[]) => {
    return genes.flatMap(gene => entrezMap?.[gene.value]?.e || []);
  };

  const handleDeleteAll = () => {
    setSelectedGenes([]);
    setQueryType(QGiQueryType.All);  // Default back to ALL when selecting new gene
    setSelectedScreen(null);
    setCurrentGene(undefined);
  }

  const handleDeleteGene = (gene: SearchOption) => {
    setSelectedGenes(selectedGenes.filter(g => g.value !== gene.value));
  };

  /** Update currentGene and set dropdown filters to default whenever a new gene is selected from the list */
  const handleSelectGene = (gene: SearchOption) => {
    setQueryType(QGiQueryType.All);
    setSelectedScreen(null);
    setCurrentGene(gene);
  }

  const handleSelectType = (gene: SearchOption, type: QGiQueryType, screen?: string) => {
    if (gene.value !== currentGene?.value) setCurrentGene(gene);  // In case we click dropdown of unselected gene
    setQueryType(type);
    setSelectedScreen(screen ?? "");
  };

  const handleDownload = (entrezIDs: number[]) => {
    const alert = {
      type: AlertType.Info,
      message: "Download of Interaction and Profile Similarity data will start shortly.",
    };

    addAlert(alert);
    downloadScores({queries: entrezIDs}).finally(() => removeAlert(alert));
  }

  const handleHeatmap = (entrezIDs: number[]) => {
    const alert = {
      type: AlertType.Info,
      message: "Download of Interaction Heatmap will start shortly.",
    };

    addAlert(alert);
    downloadHeatmap({queries: entrezIDs}).finally(() => removeAlert(alert));
  }

  if (!entrezMap) return null;

  // Keep selection order appended for network search boxes but show newest picks first in List View
  const displayGenes = [...selectedGenes].reverse();

  return (
    <div className="list-gene-picker h-100 d-flex flex-column h-min-0">
      <div className="list-header text-start d-flex justify-content-between align-items-center btn btn-primary border-bl-0 border-br-0">
        <span>Selected Genes</span>
        <span className="d-flex gap-0">
          {selectedGenes.length > 1  && (
            <>
              <OverlayTrigger overlay={<Tooltip>qGI Heatmap</Tooltip>}>
                <Button
                  variant="outline-success border-0 p-1"
                  onClick={() => handleHeatmap(getEntrezIDs(selectedGenes))}
                >
                  <HiViewGrid/>
                </Button>
              </OverlayTrigger>
            </>
          )}
          <OverlayTrigger overlay={<Tooltip>Remove all genes</Tooltip>}>
            <Button variant="outline-danger border-0 p-1" onClick={handleDeleteAll}><IoClose/></Button>
          </OverlayTrigger>
          <OverlayTrigger
            overlay={
            <Tooltip>
              {selectedGenes.length > 10
                ? "WARNING: Maximum gene count for bulk download exceeded. " +
                "Only data for the first 10 genes will be retrieved."
                : "Download data"
              }
            </Tooltip>
            }
          >
            <Button variant="outline-primary border-0 p-1"
                    onClick={() => handleDownload(getEntrezIDs(selectedGenes.slice(0, 10)))}
                    disabled={selectedGenes.length === 0}>
              <FiDownload/>
            </Button>
          </OverlayTrigger>
        </span>
      </div>

      <div className="flex-fill overflow-y-auto h-min-0">
        <ButtonGroup vertical className="narrow-split-buttons w-100">
          {/* Blend borders between header bottom and first gene item */}
          {displayGenes.map((gene, idx) => {
            const firstClass = "";
            const innerFirstClass = idx === 0 ? "rounded-top-0" : "";
            return (
                <Dropdown
                  key={`dropdown-${gene.value}`}
                  as={ButtonGroup}
                  className={`btn-gene-item ${getActive(gene)} ${firstClass}`}
                >
              <Button variant="light" title={gene.symbol} className={`text-start ${innerFirstClass}`}
                      onClick={() => handleSelectGene(gene)}>
                {gene.symbol}
                {getActive(gene) && listState.dataType !== DataType.Correlations && (
                  <small className="text-muted ms-1">
                    {listState.queryType === QGiQueryType.Screen
                      ? selectedScreen
                      : `(${capitalize(listState.queryType)})`}
                  </small>
                )}
              </Button>
              <Dropdown.Toggle split variant="light" className={innerFirstClass} />
              <Dropdown.Menu>
                {listState.dataType !== DataType.Correlations && (
                  <>
                    {commonSelectItems.map((item) => (
                      <Dropdown.Item key={item.type} onClick={() => handleSelectType(gene, item.type)}>
                        {item.label}
                      </Dropdown.Item>
                    ))}
                    {getScreens(gene).length > 1 && getScreens(gene).map((screen) => (
                      <Dropdown.Item
                        key={`select-${screen}`}
                        onClick={() => handleSelectType(gene, QGiQueryType.Screen, screen.split(/\s+/)[1])}>
                        {screen}
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Divider/>
                  </>
                )}
                <Dropdown.Item onClick={() => handleDeleteGene(gene)}>Remove Gene</Dropdown.Item>
                <Dropdown.Item onClick={() => handleDownload([gene.value])}>Download Data</Dropdown.Item>
                <Dropdown.Item onClick={() => openGeneCards(gene.value)}>View GeneCards</Dropdown.Item>
                <Dropdown.Item onClick={() => openDepMap(gene.symbol)}>View DepMap</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )})}
        </ButtonGroup>
      </div>
    </div>
  );
}
export default ListGenePicker;
